import React, { FC, FormEvent, LabelHTMLAttributes, ReactNode } from 'react';
import classnames from 'classnames';
import { Error } from 'components';
import styles from './TextInput.module.scss';
import { t } from 'utils';

export interface ITextInput extends LabelHTMLAttributes<HTMLLabelElement> {
  name: string;
  value: string;
  label: string;
  type?: string;
  error?: boolean;
  active?: boolean;
  touched?: boolean;
  disabled?: boolean;
  required?: boolean;
  errorText?: string;
  onBlur?: () => void;
  hideErrors?: boolean;
  onFocus?: () => void;
  submitError?: string;
  maxLength?: number;
  errorClassname?: string;
  onChangeAddOn?: (event: FormEvent) => void;
  onChange?: (event: FormEvent) => void;
  modifiedSinceLastSubmit?: boolean;
  addon?: string | ReactNode;
  labelVariable?: { variable: string; value: string; translate?: boolean };
  placeholderVariable?: { variable: string; value: string; translate?: boolean };
}

const TextInput: FC<ITextInput> = (props) => {
  const {
    name,
    value,
    label,
    error,
    addon,
    active,
    // touched,
    disabled,
    required,
    className,
    maxLength,
    hideErrors,
    submitError,
    // errorText,
    labelVariable,
    type = 'text',
    errorClassname,
    placeholder = '',
    onChangeAddOn,
    onBlur = () => {},
    onFocus = () => {},
    onChange = () => {},
    placeholderVariable,
    modifiedSinceLastSubmit,
  } = props;
  const rootClassName = classnames(styles.root, className, {
    [styles.focused]: active,
    [styles.disabled]: disabled,
    [styles.error]: !modifiedSinceLastSubmit && (submitError ?? error),
    [styles.filled]: value && !active,
  });
  let labelText = '';
  if (label || labelVariable) {
    const textToReplace = labelVariable
      ? [
          {
            key: labelVariable.variable,
            value: labelVariable.value,
            translateReplace: labelVariable.translate,
          },
        ]
      : [];
    labelText = t(label, textToReplace);
  }
  let placeholderText;
  if (placeholder || placeholderVariable) {
    const textToReplace = placeholderVariable
      ? [
          {
            key: placeholderVariable.variable,
            value: placeholderVariable.value,
            translateReplace: placeholderVariable.translate,
          },
        ]
      : [];
    placeholderText = t(placeholder, textToReplace);
  }

  return (
    <div className={rootClassName}>
      {label && (
        <label className={styles.label}>
          {labelText}
          {required && ' *'}
        </label>
      )}
      <input
        disabled={disabled}
        value={value}
        className={styles.input}
        name={name}
        type={type}
        maxLength={maxLength}
        onChange={(e: FormEvent) => {
          onChange(e);
          onChangeAddOn?.(e);
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholderText}
        autoComplete='off'
      />
      {addon && <div className={styles.addon}>{addon}</div>}
      {Array.isArray(error) ? (
        error.map((fieldError, i) => (
          <Error
            key={i}
            className={classnames('mt-1', errorClassname)}
            isError={!hideErrors && !modifiedSinceLastSubmit && (submitError ?? fieldError)}
          >
            {submitError ?? fieldError}
          </Error>
        ))
      ) : (
        <Error
          className={classnames('mt-1', errorClassname)}
          isError={!hideErrors && !modifiedSinceLastSubmit && (submitError ?? error)}
        >
          {submitError ?? error}
        </Error>
      )}
    </div>
  );
};

export default TextInput;
